/**
 * The ledger's *selection*, as opposed to its cursor.
 *
 * `gridCursor` answers "where is the one cell the keyboard is pointing at". This answers "which
 * cells are highlighted", which in a spreadsheet is a different and larger question: a shift-drag
 * covers a block of them, and ctrl-clicking builds up a handful that are nowhere near each other.
 * The reason to have it at all is the readout at the bottom of the screen -- selecting the six
 * grocery rows and reading their sum off a status bar is the one thing her sheet did that a table
 * of transactions cannot, and it is the whole point of the feature.
 *
 * A selection is `{ anchor, cursor, ranges }`:
 *
 *   anchor  the cell the newest range grew from -- where shift-extension measures from
 *   cursor  where the keyboard is, always inside the newest range
 *   ranges  rectangles, oldest first, the last one being the one shift extends
 *
 * A range is `{ pane, top, bottom, left, right }`, in the *column* coordinates `gridCursor` uses,
 * with both edges inclusive. Columns rather than cell indices, again because of spans: a range is
 * a region of the sheet, and which cells happen to cover that region differs row by row.
 *
 * Ranges never span two panes. The two tables have different row counts and nothing lines up
 * across them -- there is no rectangle to describe -- so an extension that would cross is refused
 * rather than clamped into something arbitrary. Ctrl-clicking in the other pane is how you select
 * in both at once, and that is a second range, not a wider one.
 */

import { columnOfCell } from './gridCursor';

/** The columns a cell covers, as an inclusive `[first, last]` pair. */
export function cellColumns(row, cellIndex) {
	const first = columnOfCell(row, cellIndex);
	return [first, first + (row.cells[cellIndex].span || 1) - 1];
}

function rangeBetween(anchor, cursor) {
	return {
		pane: anchor.pane,
		top: Math.min(anchor.row, cursor.row),
		bottom: Math.max(anchor.row, cursor.row),
		left: Math.min(anchor.column, cursor.column),
		right: Math.max(anchor.column, cursor.column),
	};
}

/** A fresh selection of exactly one cell -- what an unmodified click leaves behind. */
export function selectionAt(cursor) {
	if(!cursor) {
		return null;
	}
	return { anchor: cursor, cursor, ranges: [rangeBetween(cursor, cursor)] };
}

/**
 * Grow the newest range out to `cursor` -- shift-click, shift-arrow, and a drag.
 *
 * Earlier ranges are left exactly as they were, so ctrl-clicking three cells and then shift-arrowing
 * widens the third rather than starting over, which is what a spreadsheet does.
 */
export function extendSelectionTo(selection, cursor) {
	if(!selection) {
		return selectionAt(cursor);
	}
	// Crossing panes: nothing to extend into. Left where it was rather than moving the cursor
	// somewhere the highlight cannot follow.
	if(!cursor || cursor.pane !== selection.anchor.pane) {
		return selection;
	}
	const ranges = selection.ranges.slice(0, -1);
	ranges.push(rangeBetween(selection.anchor, cursor));
	return { anchor: selection.anchor, cursor, ranges };
}

/** Start a new range at `cursor`, keeping everything already selected -- a ctrl-click. */
export function addSelectionAt(selection, cursor) {
	if(!selection || !cursor) {
		return selectionAt(cursor);
	}
	return { anchor: cursor, cursor, ranges: selection.ranges.concat(rangeBetween(cursor, cursor)) };
}

function coversRow(range, rowIndex) {
	return rowIndex >= range.top && rowIndex <= range.bottom;
}

function coversColumns(range, first, last) {
	return first <= range.right && last >= range.left;
}

/**
 * Is this cell highlighted?
 *
 * A cell counts as inside a range when it *overlaps* it rather than when it fits within it, which
 * is what makes a spanning cell behave: a group heading four columns wide is highlighted by a
 * selection that touches any one of them, the same way a merged cell in Excel is.
 */
export function isCellSelected(selection, paneIndex, row, cellIndex) {
	if(!selection || !row) {
		return false;
	}
	const [first, last] = cellColumns(row, cellIndex);
	return selection.ranges.some((range) => range.pane === paneIndex
		&& coversRow(range, row.index)
		&& coversColumns(range, first, last));
}

/**
 * Every cell the selection covers, once each.
 *
 * Overlapping ranges are the reason for the de-duplication rather than tidiness: ctrl-clicking a
 * cell that is already inside an earlier range, or shift-extending back over one, would otherwise
 * count its amount twice and quietly make the total wrong.
 *
 * Rows past the end of a pane are skipped rather than clamped -- unlike the cursor, which clamps so
 * that something stays selected. A selection that has lost rows should shrink; growing it onto
 * whatever row is last now would put money in the total that was never selected.
 */
export function selectedCells(panes, selection) {
	const found = [];
	if(!selection || !panes) {
		return found;
	}
	const seen = new Set();
	selection.ranges.forEach((range) => {
		const pane = panes[range.pane];
		if(!pane || !pane.rows) {
			return;
		}
		const last = Math.min(range.bottom, pane.rows.length - 1);
		for(let rowIndex = Math.max(range.top, 0); rowIndex <= last; rowIndex++) {
			const row = pane.rows[rowIndex];
			row.cells.forEach((cell, cellIndex) => {
				const [first, lastColumn] = cellColumns(row, cellIndex);
				if(!coversColumns(range, first, lastColumn)) {
					return;
				}
				const key = `${range.pane}:${rowIndex}:${cellIndex}`;
				if(seen.has(key)) {
					return;
				}
				seen.add(key);
				found.push({ paneIndex: range.pane, rowIndex, row, cellIndex, cell });
			});
		}
	});
	return found;
}

function amountOf(cell) {
	return typeof cell.amount === 'number' && Number.isFinite(cell.amount) ? cell.amount : null;
}

/**
 * What the status bar says.
 *
 * `count` is every selected cell and `numeric` only the ones carrying money, which is the
 * distinction Excel's own status bar draws: dragging across a block of rows selects names and
 * dates along with the amounts, and the sum is of the amounts alone.
 *
 * The average is rounded to whole cents. A third of $1.00 is not 33.33 cents in any sense the rest
 * of this app would recognise -- every amount here is an integer number of cents, and a display
 * that suggests otherwise invites arithmetic that does not add up.
 *
 * @returns {{count: number, numeric: number, sum: number, average: number|null}}
 */
export function summariseSelection(panes, selection) {
	const cells = selectedCells(panes, selection);
	const amounts = cells.map(({ cell }) => amountOf(cell)).filter((amount) => amount !== null);
	const sum = amounts.reduce((total, amount) => total + amount, 0);
	return {
		count: cells.length,
		numeric: amounts.length,
		sum,
		average: amounts.length ? Math.round(sum / amounts.length) : null,
	};
}
